import React, { useState, useEffect } from 'react';
import './LeaveRequestInlineCalendar.css';

/**
 * Inline Date Range Calendar for Leave Request Modal
 * Always visible calendar for selecting leave start and end dates
 */
const LeaveRequestInlineCalendar = ({ 
  startDate,
  endDate,
  onDateSelect,
  minDate = new Date(),
  blockedDates = new Set()
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectionPhase, setSelectionPhase] = useState('start');
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    
    // If start date is provided but no end date, set phase to 'end' for next click
    if (startDate && !endDate) {
      setSelectionPhase('end');
    } else if (!startDate && !endDate) {
      setSelectionPhase('start');
    }
  }, [startDate, endDate]);

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Handle date selection
  const handleDateClick = (day) => {
    const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    
    // Check if date is before minDate
    if (selectedDate < minDate) {
      return;
    }

    if (selectionPhase === 'start') {
      setTempStartDate(selectedDate);
      setTempEndDate(null);
      setSelectionPhase('end');
      onDateSelect(selectedDate, null);
    } else {
      // When selecting end date
      if (tempStartDate && selectedDate < tempStartDate) {
        // If selected date is before start date, swap them
        setTempEndDate(tempStartDate);
        setTempStartDate(selectedDate);
        setSelectionPhase('start');
        onDateSelect(selectedDate, tempStartDate);
      } else {
        setTempEndDate(selectedDate);
        setSelectionPhase('start');
        onDateSelect(tempStartDate, selectedDate);
      }
    }
  };

  // Clear selection
  const handleClear = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    setSelectionPhase('start');
    onDateSelect(null, null);
  };

  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = (firstDay.getDay() + 6) % 7; // Monday=0, Sunday=6
    
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    const calendarDays = Array(firstDayOfWeek).fill(null);
    
    for (let day = 1; day <= totalDays; day++) {
      calendarDays.push(day);
    }
    
    return calendarDays;
  };

  // Check if a day is in the selected range
  const isDayInRange = (day) => {
    if (!day || !tempStartDate) return false;
    
    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    currentDate.setHours(0, 0, 0, 0);
    
    const compareStart = new Date(tempStartDate);
    compareStart.setHours(0, 0, 0, 0);
    
    if (!tempEndDate || selectionPhase === 'end') {
      return currentDate.getTime() === compareStart.getTime();
    }
    
    const compareEnd = new Date(tempEndDate);
    compareEnd.setHours(0, 0, 0, 0);
    
    return currentDate >= compareStart && currentDate <= compareEnd;
  };

  // Check if a day is the start of the range
  const isStartDay = (day) => {
    if (!day || !tempStartDate) return false;
    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    // Normalize both dates to midnight for comparison
    currentDate.setHours(0, 0, 0, 0);
    const compareStart = new Date(tempStartDate);
    compareStart.setHours(0, 0, 0, 0);
    return currentDate.getTime() === compareStart.getTime();
  };

  // Check if a day is the end of the range
  const isEndDay = (day) => {
    if (!day || !tempEndDate) return false;
    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    // Normalize both dates to midnight for comparison
    currentDate.setHours(0, 0, 0, 0);
    const compareEnd = new Date(tempEndDate);
    compareEnd.setHours(0, 0, 0, 0);
    return currentDate.getTime() === compareEnd.getTime();
  };

  // Check if a day is today
  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return (
      today.getDate() === day && 
      today.getMonth() === currentMonth.getMonth() && 
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  // Check if a day is disabled (before minDate or has existing leave)
  const isDisabled = (day) => {
    if (!day) return true;
    const currentDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    currentDate.setHours(0, 0, 0, 0);
    
    // Check if before minDate
    const compareMin = new Date(minDate);
    compareMin.setHours(0, 0, 0, 0);
    if (currentDate < compareMin) return true;
    
    // Check if date has existing leave (blocked)
    // Format date manually to avoid timezone issues with toISOString()
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const dayNum = String(currentDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${dayNum}`;
    
    if (blockedDates && blockedDates.has(dateStr)) {
      return true;
    }
    
    return false;
  };

  // Get cell class based on its state
  const getCellClass = (day) => {
    if (!day) return 'inline-cal-empty-cell';
    
    let classes = ['inline-cal-day-cell'];
    
    if (isDisabled(day)) classes.push('inline-cal-disabled');
    if (isStartDay(day)) classes.push('inline-cal-range-start inline-cal-selected');
    if (isEndDay(day)) classes.push('inline-cal-range-end inline-cal-selected');
    if (!isStartDay(day) && !isEndDay(day) && isDayInRange(day)) classes.push('inline-cal-in-range');
    if (isToday(day)) classes.push('inline-cal-today');
    
    return classes.join(' ');
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ];
  
  const dayNames = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
  
  const currentMonthName = monthNames[currentMonth.getMonth()];
  const currentYear = currentMonth.getFullYear();
  const calendarDays = generateCalendarDays();

  return (
    <div className="inline-calendar-container">
      <div className="inline-cal-header">
        <button 
          type="button" 
          className="inline-cal-nav-button" 
          onClick={goToPreviousMonth}
          aria-label="Previous month"
        >
          <span>&#10094;</span>
        </button>
        
        <div className="inline-cal-header-title">
          {currentMonthName} {currentYear}
        </div>
        
        <button 
          type="button" 
          className="inline-cal-nav-button" 
          onClick={goToNextMonth}
          aria-label="Next month"
        >
          <span>&#10095;</span>
        </button>
      </div>
      
      <div className="inline-cal-selection-info">
        {selectionPhase === 'start' ? 'Select start date' : 'Select end date'}
      </div>
      
      <div className="inline-cal-days-header">
        {dayNames.map((day, index) => (
          <div key={index} className="inline-cal-day-name">{day}</div>
        ))}
      </div>
      
      <div className="inline-cal-days-grid">
        {calendarDays.map((day, index) => (
          <div 
            key={index} 
            className={getCellClass(day)}
            onClick={() => day && !isDisabled(day) && handleDateClick(day)}
          >
            {day}
          </div>
        ))}
      </div>
      
      <div className="inline-cal-footer">
        <button 
          type="button" 
          className="inline-cal-action-button" 
          onClick={() => {
            const today = new Date();
            handleDateClick(today.getDate());
          }}
        >
          Today
        </button>
        
        <button 
          type="button" 
          className="inline-cal-action-button" 
          onClick={handleClear}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

export default LeaveRequestInlineCalendar;

